import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created! Let's add your car.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12 order-2 lg:order-1">
        <div className="w-full max-w-sm vp-fade-up">
          <Link to="/"><Logo /></Link>
          <h1 className="mt-8 font-display text-3xl font-bold tracking-tight">Get your VecPadi</h1>
          <p className="mt-1 text-sm text-muted-foreground">Free to start. No card needed.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="register-name" required value={form.name} onChange={set("name")}
                placeholder="Chidi Okeke" className="mt-1.5 h-12 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email" type="email" required value={form.email} onChange={set("email")}
                placeholder="you@email.com" className="mt-1.5 h-12 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input id="phone" data-testid="register-phone" value={form.phone} onChange={set("phone")}
                placeholder="0803 000 0000" className="mt-1.5 h-12 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")}
                placeholder="At least 6 characters" className="mt-1.5 h-12 rounded-xl" />
            </div>
            <Button data-testid="register-submit" type="submit" disabled={loading}
              className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-base">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Create account"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="font-semibold text-emerald-600">Log in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative bg-zinc-950 order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1604447199408-9798f9f64f88?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="" className="h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white bg-gradient-to-t from-zinc-950 to-transparent">
          <h2 className="font-display text-3xl font-bold">One app for your whole car life.</h2>
          <p className="mt-2 text-emerald-400 font-semibold">We've got you covered.</p>
        </div>
      </div>
    </div>
  );
}
